// workletFx.js — an effect that runs in the audio engine, in a voice
// -----------------------------------------------
// fuzz: and sub: (fuzz.js, sub.js) are AudioWorklet processors. The engine
// loads a processor's module once and asynchronously, so a voice gets
// { input, output } joined straight through until it is ready, and the
// processor is spliced in between then.

const loaded = new Map();           // url -> WeakMap(AudioContext -> Promise)

/** Load a processor's module into this context's audio engine (once). */
export function loadWorklet(ctx, url) {
  if (!ctx?.audioWorklet) return Promise.reject(new Error("no AudioWorklet"));
  let byCtx = loaded.get(url);
  if (!byCtx) loaded.set(url, byCtx = new WeakMap());
  let p = byCtx.get(ctx);
  if (!p) {
    p = ctx.audioWorklet.addModule(url);
    p.loaded = false;
    p.then(() => { p.loaded = true; }).catch(err => console.warn("[audio] not loaded:", url, err));
    byCtx.set(ctx, p);
  }
  return p;
}

/**
 * One effect in one voice: { input, output, set(settings), stop() }.
 * `settings` are what the processor reads (processorOptions.settings, and
 * a message on each set()). `channels`: 2 on an fx bus, its left and right
 * each their own (a voice is mono here: panned after).
 */
export function buildWorkletFx(ctx, { processor, url, settings, channels = 1 }) {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const p = loadWorklet(ctx, url);
  let node = null, stopped = false;
  const make = () => {
    if (stopped || node) return;
    node = new AudioWorkletNode(ctx, processor, {
      numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [channels],
      processorOptions: { settings },
    });
    try { input.disconnect(output); } catch { /* not joined */ }
    input.connect(node).connect(output);
  };
  if (p.loaded) make();
  else { input.connect(output); p.then(make).catch(() => { /* stays as it is */ }); }
  return {
    input, output,
    set(s) { settings = s; node?.port.postMessage(settings); },
    stop() {
      stopped = true;
      for (const n of [input, node, output]) { try { n?.disconnect(); } catch { /* gone */ } }
    },
  };
}
